import { useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";

// @mui components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import { useTheme, useMediaQuery } from "@mui/material";

// Argon Dashboard components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Sidenav components
import SidenavItem from "examples/Sidenav/SidenavItem";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavFooter from "examples/Sidenav/SidenavFooter";

// Custom styles
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Context
import { useArgonController, setMiniSidenav } from "context";

function Sidenav({ color = "info", brand = "", brandName, routes, ...rest }) {
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, darkSidenav, layout } = controller;
  const location = useLocation();
  const { pathname } = location;
  const activeRoute = pathname.split("/")[1];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // Hide sidenav on mobile - mobile navigation component will handle it
  if (isMobile) {
    return null;
  }

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const renderRoutes = routes.filter(route => route.type !== "hidden").map(({ type, name, icon, title, key, href, route, collapse }) => {
    if (type === "collapse") {
      return (
        <SidenavCollapse key={key} icon={icon} name={name} active={false}>
          {collapse.map(({ name, route, icon, key: childKey }) => (
            <NavLink to={route} key={childKey}>
              <SidenavItem
                name={name}
                icon={icon || <Icon fontSize="small">fiber_manual_record</Icon>}
                active={pathname === route}
                nested={true}
              />
            </NavLink>
          ))}
        </SidenavCollapse>
      );
    }

    if (type === "route") {
      if (href) {
        return (
          <Link href={href} key={key} target="_blank" rel="noreferrer">
            <SidenavItem name={name} icon={icon} active={activeRoute === key} />
          </Link>
        );
      } else {
        return (
          <NavLink to={route} key={key}>
            <SidenavItem name={name} icon={icon} active={activeRoute === key} />
          </NavLink>
        );
      }
    }

    if (type === "title") {
      return (
        <ArgonTypography
          key={key}
          color={darkSidenav ? "white" : "dark"}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </ArgonTypography>
      );
    }

    if (type === "divider") {
      return <Divider key={key} light={darkSidenav} />;
    }

    return null;
  });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ darkSidenav, miniSidenav, layout }}>
      <ArgonBox pt={3} pb={1} px={4} textAlign="center">
        <ArgonBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={2.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <ArgonTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox component={NavLink} to="/dashboard" display="flex" alignItems="center" justifyContent="center">
          {brand && (
            <ArgonBox component="img" src={brand} alt="PRM Logo" width="5rem" />
          )}
        </ArgonBox>
      </ArgonBox>
      <Divider light={darkSidenav} />
      <List
        sx={{
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {renderRoutes}
      </List>
      <ArgonBox pt={5} mt="auto" mb={5} mx={2}>
        <SidenavFooter />
      </ArgonBox>
    </SidenavRoot>
  );
}


Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
